import { Table, TableColumnsType, TableProps, Tag } from "antd";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { ResizeCallbackData } from "react-resizable";
import { useAppSelector } from "store";
import { Post, PostStatus } from "store/posts/postConstants";
import { currentBusinessGetter } from "store/user/userSlice";
import ResizableTableHeader from "./ResizableTableHeader";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

interface PostTableDisplayProps<T extends Post = Post> {
  postList: T[];
  onRowClick?: (post: T) => void;
}

const PostCardTable = <T extends Post = Post>({
  postList,
  onRowClick,
}: PostTableDisplayProps<T>) => {
  const currentBusiness = useAppSelector(currentBusinessGetter);
  const [selectedPostKeys, setSelectedPostKeys] = useState<React.Key[]>([]);
  const [columnWidths, setColumnWidths] = useState({
    media: 100,
    caption: 300,
    topic: 150,
    status: 100,
    created_at: 150,
  });

  const topicFilterOptions = useMemo(() => {
    const allBusinessTopics = currentBusiness?.topics || [];
    const uniqueTitles = [
      ...new Set(allBusinessTopics.map((topic) => topic.title).filter(Boolean)),
    ];
    return uniqueTitles.map((title) => ({
      text: title,
      value: title,
    }));
  }, [currentBusiness?.topics]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedPostKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<Post> = {
    selectedRowKeys: selectedPostKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: "odd",
        text: "Select Odd Rows",
        onSelect: () => {
          const newSelectedRowKeys = postList
            .map((post) => post.id)
            .filter((_key, index) => index % 2 === 0);
          setSelectedPostKeys(newSelectedRowKeys);
        },
      },
      {
        key: "even",
        text: "Select Even Rows",
        onSelect: () => {
          const newSelectedRowKeys = postList
            .map((post) => post.id)
            .filter((_key, index) => index % 2 !== 0);
          setSelectedPostKeys(newSelectedRowKeys);
        },
      },
    ],
  };

  const handleResize =
    (key: keyof typeof columnWidths) =>
    (_e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
      setColumnWidths((prev) => ({
        ...prev,
        [key]: size.width,
      }));
    };

  const baseColumns: TableColumnsType<Post> = [
    {
      title: "Media",
      key: "media",
      render: (post: Post) => {
        const imageUrl = post.thumbnail_url || post.media_urls?.[0];
        return imageUrl ? (
          <img
            src={imageUrl}
            alt="Post media"
            className="w-24 h-24 object-cover rounded"
          />
        ) : (
          "-"
        );
      },
    },
    {
      title: "Caption",
      dataIndex: "caption",
      key: "caption",
      render: (caption: string) => (
        <div className="max-h-32 overflow-y-auto">
          <span className="whitespace-pre-wrap block">{caption || "-"}</span>
        </div>
      ),
    },
    {
      title: "Topic",
      key: "topic",
      render: (post: Post) => post.inputs?.topic || "-",
      filters: topicFilterOptions,
      onFilter: (value, record) => record.inputs?.topic === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: PostStatus) => <Tag>{status}</Tag>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD HH:mm"),
      sorter: (a: Post, b: Post) =>
        dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      defaultSortOrder: "descend" as const,
    },
  ];

  const resizableColumns = baseColumns.map((col) => {
    if (!col.key) return col;
    const colKey = col.key as keyof typeof columnWidths;
    return {
      ...col,
      width: columnWidths[colKey],
      onHeaderCell: (column: TableColumnsType<Post>[number]) => ({
        width: (column as any).width,
        onResize: handleResize(colKey),
      }),
    };
  }) as TableColumnsType<Post>;

  return (
    <div className="mx-4 [&_.react-resizable]:relative [&_.react-resizable]:bg-clip-padding [&_.react-resizable-handle]:absolute [&_.react-resizable-handle]:right-[-5px] [&_.react-resizable-handle]:bottom-0 [&_.react-resizable-handle]:z-[1] [&_.react-resizable-handle]:w-[10px] [&_.react-resizable-handle]:h-full [&_.react-resizable-handle]:cursor-col-resize">
      <Table<Post>
        rowSelection={rowSelection}
        dataSource={postList}
        columns={resizableColumns}
        rowKey="id"
        components={{
          header: { cell: ResizableTableHeader },
        }}
        bordered
        pagination={false}
        onRow={(record) => ({
          onClick: () => {
            if (onRowClick && record.status !== "PUBLISHED") {
              onRowClick(record as T);
            }
          },
        })}
        rowClassName={(record) =>
          record.status !== "PUBLISHED" ? "cursor-pointer hover:bg-gray-50" : ""
        }
      />
    </div>
  );
};

export default PostCardTable;
